import React, { useEffect } from 'react';
import { ApolloQueryResult, useMutation } from '@apollo/client';
import { Checkbox, Form, Input, Modal, Select } from 'antd';
// Api
import { EDIT_CAMPAIGN_CONTENT } from 'api/campaignV2/mutations';
// Types
import {
  GetCampaignsV2,
  GetCampaignsV2Variables,
} from 'api/campaignV2/types/GetCampaignsV2';
import {
  EditCampaignContent,
  EditCampaignContentVariables,
} from 'api/campaignV2/types/EditCampaignContent';
// Helpers
import {
  CampaignContentFormValues,
  ContentEditableActivity,
  getCampaignContentFormValues,
  getEditCampaignContentInput,
  getRequiredIfSetRules,
} from 'helpers/cbActivity';
// Ui
import { successNotification, errorNotification } from 'ui/Notification';
// Style
import styles from './EditCampaignContentModal.module.scss';

const { TextArea } = Input;

type EditCampaignContentModalProps = {
  show: boolean;
  handleClose: () => void;
  campaignId: string;
  activity: ContentEditableActivity;
  refetch: (
    variables?: Partial<GetCampaignsV2Variables> | undefined
  ) => Promise<ApolloQueryResult<GetCampaignsV2>>;
};

const EditCampaignContentModal = ({
  show,
  handleClose,
  campaignId,
  activity,
  refetch,
}: EditCampaignContentModalProps): JSX.Element => {
  const [form] = Form.useForm<CampaignContentFormValues>();
  const initialValues = getCampaignContentFormValues(activity);

  const [editCampaignContent, { loading }] = useMutation<
    EditCampaignContent,
    EditCampaignContentVariables
  >(EDIT_CAMPAIGN_CONTENT);

  useEffect(() => {
    if (show) {
      form.resetFields();
    }
  }, [show, form]);

  const handleSubmit = async () => {
    try {
      await form.validateFields();
    } catch {
      return;
    }
    const values: CampaignContentFormValues = form.getFieldsValue(true);

    try {
      await editCampaignContent({
        variables: { input: getEditCampaignContentInput(campaignId, values) },
      });
    } catch (error) {
      console.error('Error updating campaign content', error);
      errorNotification('Error updating campaign content');
      return;
    }

    successNotification('Campaign content has been updated');
    handleClose();
    refetch();
  };

  return (
    <Modal
      title="Edit campaign content"
      visible={show}
      onCancel={handleClose}
      onOk={handleSubmit}
      okText="Save"
      confirmLoading={loading}
      width={800}
      destroyOnClose
    >
      <p className={styles.hint}>
        Wording-only changes. Talent are not notified and no emails are resent.
      </p>

      <Form form={form} layout="vertical" initialValues={initialValues}>
        <div className={styles.row}>
          <Form.Item
            name="brandName"
            label="Company Name"
            rules={getRequiredIfSetRules(initialValues.brandName)}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="website"
            label="Company Website"
            rules={[{ type: 'url', message: 'Enter a valid URL' }]}
          >
            <Input placeholder="https://example.com" />
          </Form.Item>
        </div>

        <Form.Item name="instructions" label="Campaign brief">
          <TextArea autoSize={{ minRows: 4 }} />
        </Form.Item>

        <div className={styles.row}>
          <Form.Item name="encouraged" label="Encouraged">
            <TextArea autoSize={{ minRows: 3 }} />
          </Form.Item>
          <Form.Item name="avoid" label="Please avoid">
            <TextArea autoSize={{ minRows: 3 }} />
          </Form.Item>
        </div>

        <div className={styles.row}>
          <Form.Item name="promoCodes" label="Promotion codes">
            <Select mode="tags" open={false} />
          </Form.Item>
          <Form.Item name="promoUrls" label="Promotion urls">
            <Select mode="tags" open={false} />
          </Form.Item>
        </div>

        {initialValues.tasks?.map((task, taskIndex) => (
          <div key={task.id} className={styles.deliverable}>
            <p className={styles.deliverableTitle}>{`Deliverable ${
              taskIndex + 1
            }`}</p>

            <Form.Item
              name={['tasks', taskIndex, 'title']}
              label="Title"
              rules={getRequiredIfSetRules(task.title)}
            >
              <Input />
            </Form.Item>
            <Form.Item
              name={['tasks', taskIndex, 'description']}
              label="Description"
            >
              <TextArea autoSize={{ minRows: 2 }} />
            </Form.Item>
            {task.suggestedCaption !== undefined && (
              <Form.Item
                name={['tasks', taskIndex, 'suggestedCaption']}
                label="Suggested caption"
              >
                <TextArea autoSize={{ minRows: 2 }} />
              </Form.Item>
            )}

            <div className={styles.row}>
              {task.hashtags !== undefined && (
                <Form.Item
                  name={['tasks', taskIndex, 'hashtags']}
                  label="Hashtags"
                >
                  <Select mode="tags" open={false} />
                </Form.Item>
              )}
              {task.mentions !== undefined && (
                <Form.Item
                  name={['tasks', taskIndex, 'mentions']}
                  label="Mentions"
                >
                  <Select mode="tags" open={false} />
                </Form.Item>
              )}
            </div>

            {task.tagAsCollab !== undefined && (
              <Form.Item
                name={['tasks', taskIndex, 'tagAsCollab']}
                valuePropName="checked"
              >
                <Checkbox>Tag as collaborator</Checkbox>
              </Form.Item>
            )}

            {task.contentItems?.map((item, itemIndex) => (
              <div key={item.id} className={styles.contentItem}>
                <p className={styles.contentItemTitle}>{`Content ${
                  itemIndex + 1
                }`}</p>
                <Form.Item
                  name={[
                    'tasks',
                    taskIndex,
                    'contentItems',
                    itemIndex,
                    'title',
                  ]}
                  label="Content Description"
                  rules={getRequiredIfSetRules(item.title)}
                >
                  <Input />
                </Form.Item>
                <Form.Item
                  name={[
                    'tasks',
                    taskIndex,
                    'contentItems',
                    itemIndex,
                    'description',
                  ]}
                  label="Instructions"
                >
                  <TextArea autoSize={{ minRows: 2 }} />
                </Form.Item>
              </div>
            ))}
          </div>
        ))}
      </Form>
    </Modal>
  );
};

export default EditCampaignContentModal;
